import { Button } from "@/components/ui/button";
import { Trash, ArrowCounterClockwise, X, Warning, DownloadSimple, FolderSimple, Copy } from "@phosphor-icons/react";

/**
 * Sticky action bar that appears whenever the user has selected ≥1 row in any
 * list with checkbox-based bulk operations. Shared by Cases, Quotes, Invoices,
 * Documents and Tasks lists.
 *
 * Props:
 *  - count       : number of selected items
 *  - onClear     : clear selection
 *  - onDelete    : optional handler for soft-delete bulk action
 *  - onRestore   : optional handler for restore-from-trash bulk action
 *  - onPurge     : optional handler for hard-delete (admin) bulk action
 *  - onDownload  : optional handler for bulk download (zip)
 *  - label       : noun for the toast/copy (e.g. "case", "quote")
 *  - variant     : "active" (delete) | "trash" (restore + purge)
 *  - testIdPrefix: prefix for data-testid attributes
 */
export default function BulkActionBar({
  count, onClear, onDelete, onRestore, onPurge, onDownload, onMove, onCopy,
  label = "item", variant = "active", testIdPrefix = "bulk",
}) {
  if (!count) return null;
  return (
    <div
      className="sticky top-2 z-20 mb-4 rounded-lg px-4 py-3 flex items-center justify-between shadow-lg text-white"
      style={{ background: "#100f5d" }}
      data-testid={`${testIdPrefix}-action-bar`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-sm font-semibold">
          {count} {label}{count === 1 ? "" : "s"} selected
        </span>
        <button
          onClick={onClear}
          data-testid={`${testIdPrefix}-clear-btn`}
          className="inline-flex items-center gap-1 text-[11px] text-white/70 hover:text-white"
        >
          <X weight="bold" className="w-3.5 h-3.5" /> Clear
        </button>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {variant === "active" && onDownload && (
          <Button
            onClick={onDownload}
            size="sm"
            className="h-8 text-xs bg-white/15 hover:bg-white/25 text-white gap-1.5"
            data-testid={`${testIdPrefix}-download-btn`}
          >
            <DownloadSimple weight="bold" className="w-3.5 h-3.5" /> Download {count}
          </Button>
        )}
        {variant === "active" && onMove && (
          <Button
            onClick={onMove}
            size="sm"
            className="h-8 text-xs bg-white/15 hover:bg-white/25 text-white gap-1.5"
            data-testid={`${testIdPrefix}-move-btn`}
          >
            <FolderSimple weight="bold" className="w-3.5 h-3.5" /> Move {count}
          </Button>
        )}
        {variant === "active" && onCopy && (
          <Button
            onClick={onCopy}
            size="sm"
            className="h-8 text-xs bg-white/15 hover:bg-white/25 text-white gap-1.5"
            data-testid={`${testIdPrefix}-copy-btn`}
          >
            <Copy weight="bold" className="w-3.5 h-3.5" /> Copy {count}
          </Button>
        )}
        {variant === "active" && onDelete && (
          <Button
            onClick={onDelete}
            size="sm"
            className="h-8 text-xs bg-red-500 hover:bg-red-600 text-white gap-1.5"
            data-testid={`${testIdPrefix}-delete-btn`}
          >
            <Trash weight="bold" className="w-3.5 h-3.5" /> Delete {count}
          </Button>
        )}
        {variant === "trash" && onRestore && (
          <Button
            onClick={onRestore}
            size="sm"
            className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5"
            data-testid={`${testIdPrefix}-restore-btn`}
          >
            <ArrowCounterClockwise weight="bold" className="w-3.5 h-3.5" /> Restore {count}
          </Button>
        )}
        {variant === "trash" && onPurge && (
          <Button
            onClick={onPurge}
            size="sm"
            className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white gap-1.5"
            data-testid={`${testIdPrefix}-purge-btn`}
          >
            <Warning weight="fill" className="w-3.5 h-3.5" /> Permanently delete {count}
          </Button>
        )}
      </div>
    </div>
  );
}
